import { useState, useCallback, useEffect, useRef, type ChangeEvent } from 'react';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { useResetRollMutation, useDeleteRollMutation, useConfirmRollMutation } from '../hooks/useRolls';
import { useConfirm } from '../hooks/useConfirm';
import { useFileDrop } from '../hooks/useFileDrop';
import { acceptFiles, describeRejections, ACCEPT_ATTRIBUTE } from '../utils/fileAcceptance';
import styles from './RollCard.module.css';

export interface RollCardScan {
  scan_id: string;
  filename?: string;
  original_filename?: string;
  thumbnail_ready: boolean;
  thumbnail_url?: string;
  roll_id?: string;
  uploaded_at?: string;
}

type UploadStatus = 'pending' | 'uploading' | 'failed';

interface FileUploadState {
  file: File;
  status: UploadStatus;
  progress: number;
}

/** Aggregate upload state a card reports up so the order-level footer can gate Confirm. */
export interface RollCardStatus {
  uploading: boolean;
  pending: number;
  failed: number;
}

export interface RollCardProps {
  orderId: string;
  roll: { roll_id: string; roll_name: string; confirmed?: boolean };
  /** Persisted scans belonging to this roll (already filtered by the parent). */
  scans: RollCardScan[];
  /** Read-only cards (order already marked ready, or the "Unassigned" bucket) hide the uploader. */
  readOnly?: boolean;
  onStatusChange?: (rollId: string, status: RollCardStatus) => void;
  /** Called after an upload batch so the parent can refetch the order's scans. */
  onUploaded?: () => void;
}

/**
 * A self-contained upload surface for a single roll: its own file selector,
 * Upload/Retry buttons, per-file progress, and the roll's persisted scans.
 *
 * Each card scopes all upload state to one roll, which removes the old shared
 * "selected roll" + global file-list ambiguity of the multi-roll flow.
 */
export function RollCard({
  orderId,
  roll,
  scans,
  readOnly = false,
  onStatusChange,
  onUploaded,
}: RollCardProps) {
  const { handleUnauthorized } = useAdminAuth();
  const { confirm, confirmDialog } = useConfirm();

  const [files, setFiles] = useState<FileUploadState[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  /** Feedback about files turned away at selection time (wrong type, duplicates). */
  const [selectionNotice, setSelectionNotice] = useState<string | null>(null);
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetRoll = useResetRollMutation(orderId);
  const deleteRoll = useDeleteRollMutation(orderId);
  const confirmRoll = useConfirmRollMutation(orderId);

  const pendingCount = files.filter((f) => f.status === 'pending').length;
  const failedCount = files.filter((f) => f.status === 'failed').length;

  // Report aggregate status up so the parent can enable/disable order-level Confirm.
  useEffect(() => {
    onStatusChange?.(roll.roll_id, { uploading, pending: pendingCount, failed: failedCount });
  }, [roll.roll_id, uploading, pendingCount, failedCount, onStatusChange]);

  const handleImageError = useCallback((scanId: string) => {
    setFailedImages((prev) => new Set(prev).add(scanId));
  }, []);

  /**
   * The single entry point into the upload queue — both the file picker and the
   * drop zone route through here so the two behave identically.
   */
  const enqueueFiles = useCallback(
    (incoming: File[]) => {
      if (incoming.length === 0) return;
      const result = acceptFiles(
        incoming,
        files.map((f) => f.file)
      );
      setSelectionNotice(describeRejections(result));
      if (result.accepted.length === 0) return;
      setFiles((prev) => [
        ...prev,
        ...result.accepted.map((file) => ({
          file,
          status: 'pending' as UploadStatus,
          progress: 0,
        })),
      ]);
      setUploadError(null);
    },
    [files]
  );

  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files;
    if (!selected) return;
    enqueueFiles(Array.from(selected));
    // Allow re-selecting the same file(s) after a clear.
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const { isDragActive, isReading, dropHandlers } = useFileDrop({
    onFiles: enqueueFiles,
    disabled: readOnly || uploading,
  });

  const uploadBatch = useCallback(
    async (batch: FileUploadState[]) => {
      if (batch.length === 0) return;
      setUploading(true);
      setUploadError(null);
      // The notice describes the last selection, not this upload — clear it so
      // it doesn't linger next to unrelated progress.
      setSelectionNotice(null);

      try {
        const filenames = batch.map((f) => f.file.name);
        const res = await fetch(`/api/v1/admin/orders/${orderId}/scans`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ filenames, roll_id: roll.roll_id }),
        });

        if (res.status === 401 || res.status === 403) {
          handleUnauthorized();
          return;
        }
        if (res.status === 404) {
          setUploadError('This roll no longer exists. Reload the page.');
          return;
        }
        if (!res.ok) {
          setUploadError('Failed to get upload URLs');
          return;
        }

        const uploadUrls: { scan_id: string; upload_url: string }[] = await res.json();

        // Mark this batch as uploading.
        setFiles((prev) =>
          prev.map((f) =>
            batch.some((b) => b.file === f.file) ? { ...f, status: 'uploading', progress: 0 } : f
          )
        );

        const results = await Promise.all(
          batch.map(async (fileState, idx) => {
            const { upload_url } = uploadUrls[idx];
            const ok = await uploadWithProgress(upload_url, fileState.file, (percent) => {
              setFiles((prev) =>
                prev.map((f) => (f.file === fileState.file ? { ...f, progress: percent } : f))
              );
            });
            return { file: fileState.file, ok };
          })
        );

        // Successful uploads become persisted scans (shown below); drop them from
        // the working list. Failed uploads stay so they can be retried.
        const failedFiles = new Set(results.filter((r) => !r.ok).map((r) => r.file));
        setFiles((prev) =>
          prev
            .filter((f) => !results.some((r) => r.file === f.file) || failedFiles.has(f.file))
            .map((f) => (failedFiles.has(f.file) ? { ...f, status: 'failed', progress: 0 } : f))
        );

        onUploaded?.();
      } catch {
        setUploadError('Upload failed. Please try again.');
      } finally {
        setUploading(false);
      }
    },
    [orderId, roll.roll_id, handleUnauthorized, onUploaded]
  );

  const handleUpload = () => uploadBatch(files.filter((f) => f.status === 'pending'));

  const handleRetryFailed = () => {
    const failed = files.filter((f) => f.status === 'failed');
    if (failed.length === 0) return;
    setFiles((prev) => prev.map((f) => (f.status === 'failed' ? { ...f, status: 'pending', progress: 0 } : f)));
    uploadBatch(failed);
  };

  const rollBusy = resetRoll.isPending || deleteRoll.isPending || confirmRoll.isPending;
  const rollActionError = resetRoll.error?.message ?? deleteRoll.error?.message ?? null;

  const handleResetRoll = async () => {
    if (rollBusy) return;
    const ok = await confirm({
      title: 'Reset roll',
      message:
        `Reset roll "${roll.roll_name}"?\n\nThis permanently deletes all its uploaded scans and ` +
        'generated images but keeps the roll itself. This cannot be undone.',
      confirmLabel: 'Reset',
      destructive: true,
    });
    if (ok) resetRoll.mutate(roll.roll_id);
  };

  const handleDeleteRoll = async () => {
    if (rollBusy) return;
    const ok = await confirm({
      title: 'Delete roll',
      message:
        `Delete roll "${roll.roll_name}"?\n\nThis permanently deletes the roll and all its uploaded ` +
        'scans and generated images. This cannot be undone.',
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (ok) deleteRoll.mutate(roll.roll_id);
  };

  const scanCount = scans.length;
  const bodyId = `roll-card-body-${roll.roll_id}`;
  const dropzoneHintId = `roll-card-dropzone-hint-${roll.roll_id}`;

  const confirmed = roll.confirmed ?? false;
  const allThumbnailsReady = scanCount > 0 && scans.every((s) => s.thumbnail_ready);
  const canConfirm = !confirmed && scanCount > 0 && pendingCount === 0 && !uploading;

  const handleConfirmRoll = () => {
    if (confirmRoll.isPending) return;
    confirmRoll.mutate(roll.roll_id);
  };

  // Transient upload hints take priority; otherwise show the roll's lifecycle
  // status so a collapsed card still reveals what stage the roll is at.
  const uploadHint = uploading
    ? 'Uploading…'
    : failedCount > 0
      ? `${failedCount} failed`
      : pendingCount > 0
        ? `${pendingCount} pending`
        : null;
  const rollStatusHint = confirmed ? (allThumbnailsReady ? 'Ready' : 'Processing…') : null;
  const statusHint = uploadHint ?? rollStatusHint;

  return (
    <section className={styles.card} aria-label={`Roll ${roll.roll_name}`}>
      <header className={styles.header}>
        <h3 className={styles.rollHeading}>
          <button
            type="button"
            className={styles.toggle}
            aria-expanded={expanded}
            aria-controls={bodyId}
            onClick={() => setExpanded((prev) => !prev)}
          >
            <span
              className={`${styles.chevron}${expanded ? ` ${styles.chevronOpen}` : ''}`}
              aria-hidden="true"
            />
            <span className={styles.rollName}>{roll.roll_name}</span>
            <span className={styles.scanCount}>
              {scanCount} scan{scanCount === 1 ? '' : 's'}
            </span>
            {statusHint && <span className={styles.statusHint}>{statusHint}</span>}
          </button>
        </h3>
      </header>

      <div id={bodyId} className={styles.body} hidden={!expanded}>
      {!readOnly && (
        <div className={styles.uploader}>
          {/*
            The label doubles as the drop zone: clicking or keyboard-activating
            it opens the picker, while dragging files onto it queues them
            directly. The input stays in the DOM (visually hidden, not
            `display: none`) so it keeps its accessible name and tab stop.
          */}
          <label
            className={`${styles.dropzone}${isDragActive ? ` ${styles.dropzoneActive}` : ''}${
              uploading ? ` ${styles.dropzoneDisabled}` : ''
            }`}
            data-drag-active={isDragActive ? 'true' : undefined}
            {...dropHandlers}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPT_ATTRIBUTE}
              multiple
              onChange={handleFileSelect}
              disabled={uploading}
              className={styles.fileInput}
              aria-label={`Select files for ${roll.roll_name}`}
              aria-describedby={dropzoneHintId}
            />
            <span className={styles.dropzoneText} aria-hidden="true">
              {isReading
                ? 'Reading folder…'
                : isDragActive
                  ? 'Drop to add files'
                  : 'Drag scans or a folder here, or click to browse'}
            </span>
            <span id={dropzoneHintId} className={styles.dropzoneHint}>
              TIFF only (.tif, .tiff)
            </span>
          </label>

          {selectionNotice && (
            <p className={styles.noticeText} role="alert">
              {selectionNotice}
            </p>
          )}

          {files.length > 0 && (
            <ul className={styles.fileList}>
              {files.map((f, i) => (
                <li key={i} className={styles.fileItem}>
                  <div className={styles.fileRow}>
                    {f.status === 'uploading' ? (
                      <span className={styles.fileProgressLabel}>{f.progress}%</span>
                    ) : (
                      <span className={styles.fileStatus} style={{ color: statusColor(f.status) }}>
                        [{f.status}]
                      </span>
                    )}
                    <span className={styles.fileName}>{f.file.name}</span>
                    <span className={styles.fileSize}>({formatSize(f.file.size)})</span>
                  </div>
                  {f.status === 'uploading' && (
                    <progress
                      className={styles.uploadProgress}
                      value={f.progress}
                      max={100}
                      aria-label={`${f.file.name}: ${f.progress}% uploaded`}
                    />
                  )}
                </li>
              ))}
            </ul>
          )}

          <div className={styles.actions}>
            {pendingCount > 0 && (
              <button onClick={handleUpload} disabled={uploading}>
                {uploading ? 'Uploading…' : `Upload ${pendingCount} file${pendingCount === 1 ? '' : 's'}`}
              </button>
            )}
            {failedCount > 0 && !uploading && (
              <button onClick={handleRetryFailed}>Retry Failed ({failedCount})</button>
            )}
          </div>

          {uploadError && (
            <p className={styles.errorText} role="alert">
              {uploadError}
            </p>
          )}
        </div>
      )}

      {scanCount === 0 ? (
        <p className={styles.emptyRollMessage}>No scans uploaded yet</p>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Thumbnail</th>
                <th>Filename</th>
                <th>Thumbnail Status</th>
              </tr>
            </thead>
            <tbody>
              {scans.map((scan) => (
                <tr key={scan.scan_id}>
                  <td>
                    {scan.thumbnail_ready && scan.thumbnail_url && !failedImages.has(scan.scan_id) ? (
                      <img
                        src={scan.thumbnail_url}
                        alt={scan.original_filename || scan.filename || 'Scan thumbnail'}
                        className={styles.thumbnail}
                        loading="lazy"
                        onError={() => handleImageError(scan.scan_id)}
                      />
                    ) : (
                      <span className={styles.thumbnailPlaceholder}>
                        {failedImages.has(scan.scan_id) ? 'Unavailable' : '—'}
                      </span>
                    )}
                  </td>
                  <td>{scan.original_filename || scan.filename}</td>
                  <td>
                    {scan.thumbnail_ready ? (
                      <span className={styles.statusReady}>Ready</span>
                    ) : (
                      <span className={styles.statusProcessing}>Processing</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!readOnly && !confirmed && scanCount > 0 && (
        <div className={styles.confirmRow}>
          <button
            type="button"
            className={styles.confirmRollBtn}
            onClick={handleConfirmRoll}
            disabled={!canConfirm || confirmRoll.isPending}
          >
            {confirmRoll.isPending ? 'Confirming…' : 'Confirm uploads'}
          </button>
          {pendingCount > 0 && (
            <span className={styles.confirmHint}>Upload pending files first</span>
          )}
          {confirmRoll.error && (
            <span className={styles.errorText} role="alert">
              {confirmRoll.error.message}
            </span>
          )}
        </div>
      )}

      {!readOnly && confirmed && (
        <p className={styles.processingNote}>
          {allThumbnailsReady
            ? 'Confirmed — all thumbnails ready.'
            : 'Confirmed — generating thumbnails…'}
        </p>
      )}

      {!readOnly && (
        <div className={styles.rollActions}>
          <button
            type="button"
            className={styles.resetRollBtn}
            onClick={handleResetRoll}
            disabled={rollBusy || scanCount === 0}
          >
            {resetRoll.isPending ? 'Resetting…' : 'Reset roll'}
          </button>
          <button
            type="button"
            className={styles.deleteRollBtn}
            onClick={handleDeleteRoll}
            disabled={rollBusy}
          >
            {deleteRoll.isPending ? 'Deleting…' : 'Delete roll'}
          </button>
          {rollActionError && (
            <p className={styles.errorText} role="alert">
              {rollActionError}
            </p>
          )}
        </div>
      )}
      </div>
      {confirmDialog}
    </section>
  );
}

// --- Local helpers ---

function uploadWithProgress(
  url: string,
  file: File,
  onProgress: (percent: number) => void
): Promise<boolean> {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    xhr.upload.onprogress = (event: ProgressEvent) => {
      if (event.lengthComputable) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };
    xhr.onload = () => resolve(xhr.status >= 200 && xhr.status < 300);
    xhr.onerror = () => resolve(false);
    xhr.ontimeout = () => resolve(false);
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', 'image/tiff');
    xhr.send(file);
  });
}

function statusColor(status: UploadStatus): string {
  switch (status) {
    case 'failed':
      return '#c00';
    case 'uploading':
      return '#06c';
    default:
      return '#666';
  }
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
